import React from "react";
import { Link } from "react-router-dom";
import { useI18n, pathFor } from "@/i18n/I18nProvider";
import { ShieldCheck, Mail, Phone, MapPin, Clock } from "lucide-react";

export default function Footer() {
  const { lang, t } = useI18n();
  const home = pathFor(lang, "");
  const legal = t("footer.legal") || [];

  return (
    <footer className="border-t border-slate-200 bg-slate-950 text-slate-200" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-4 lg:px-8">
        <div>
          <Link to={home} data-testid="footer-logo-link" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-500/15 ring-1 ring-emerald-400/40">
              <ShieldCheck className="h-5 w-5 text-emerald-300" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-base font-bold tracking-tight text-white">{t("brand.name")}</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                {t("brand.tagline")}
              </span>
            </span>
          </Link>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">{t("footer.about")}</p>
          <p className="mt-4 text-xs text-slate-400">{t("footer.disclaimer")}</p>
        </div>

        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-white">
            {t("footer.contactTitle")}
          </h4>
          <ul className="mt-4 space-y-3 text-sm text-slate-300">
            <li className="flex gap-2">
              <MapPin className="mt-0.5 h-4 w-4 flex-none text-emerald-300" />
              <span>{t("footer.address")}</span>
            </li>
            <li className="flex gap-2">
              <Phone className="mt-0.5 h-4 w-4 flex-none text-emerald-300" />
              <a href={`tel:${t("footer.phone")}`} data-testid="footer-phone" className="hover:text-white">
                {t("footer.phone")}
              </a>
            </li>
            <li className="flex gap-2">
              <Mail className="mt-0.5 h-4 w-4 flex-none text-emerald-300" />
              <a href={`mailto:${t("footer.email")}`} data-testid="footer-email" className="hover:text-white">
                {t("footer.email")}
              </a>
            </li>
            <li className="flex gap-2">
              <Clock className="mt-0.5 h-4 w-4 flex-none text-emerald-300" />
              <span>{t("footer.hours")}</span>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-white">
            {t("footer.resourcesTitle")}
          </h4>
          <ul className="mt-4 space-y-2 text-sm text-slate-300">
            <li><a href={`${home}#programs`} data-testid="footer-link-programs" className="hover:text-white">{t("footer.programsLink")}</a></li>
            <li><a href={`${home}#literacy`} data-testid="footer-link-literacy" className="hover:text-white">{t("footer.literacyLink")}</a></li>
            <li><a href={`${home}#newsletter`} data-testid="footer-link-newsletter" className="hover:text-white">{t("footer.newsletterLink")}</a></li>
          </ul>
          <h4 className="mt-6 text-sm font-semibold uppercase tracking-wider text-white">
            {t("footer.linksTitle")}
          </h4>
          <ul className="mt-4 space-y-2 text-sm text-slate-300">
            <li><Link to={pathFor(lang, "/privacy")} data-testid="footer-link-privacy" className="hover:text-white">{t("footer.privacy")}</Link></li>
            <li><Link to={pathFor(lang, "/cookies")} data-testid="footer-link-cookies" className="hover:text-white">{t("footer.cookies")}</Link></li>
            <li><Link to={pathFor(lang, "/terms")} data-testid="footer-link-terms" className="hover:text-white">{t("footer.terms")}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-white">
            {t("footer.legalTitle")}
          </h4>
          <ul className="mt-4 space-y-2 text-xs text-slate-300">
            {legal.map((line, i) => (
              <li key={i} className="leading-relaxed">{line}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-4 py-5 text-xs text-slate-400 sm:flex-row sm:items-center sm:px-6 lg:px-8">
          <p data-testid="footer-copyright">{t("footer.copyright")}</p>
          <p>dinverolario.com · CDMX · MX</p>
        </div>
      </div>
    </footer>
  );
}
